import {
    TRANSFERIN_ENTEREDTYPE,
    TRANSFEROUT_MOVETYPE,
    MEASURE_REFERENCE_TYPE,
    LANDING_NAVIGATION_TABS,
    MEMBERS_TABS,
    MEMBERS_MEMBER_DETAIL_TABS,
    IN_BODY_INTERVIEW_TABS,
} from '@icoach/constants/';

/**
 * ─────────────────────────────────────────────────────────────
 * 📌 ROUTER 定義原則說明（請維護此區段）

 *
 * 📐 命名風格：
 * - ROUTER 變數名稱：CLUB_APPOINTMENT_ROUTER
 * - path 生成函數：GET_HISTORY_PATH、GET_CONTRACT_PATH
 * - tabs 常數參考：LANDING_NAVIGATION_TABS、MEMBERS_TABS
 * - 模組名稱大寫加下底線（如：CLUB_LOGIN_ROUTER）
 * - ROUTER 內部的屬性命名皆為 UPPER_SNAKE_CASE 或語義清晰小駝峰（如：LOGIN、GET_CONTRACT_PATH）
 * - 動態參數使用 `:paramName(\\d+)` 或明確 pattern 限制（例如 tabs、ID）
 * - 提供常數函式（如 GET_*_PATH）以統一路徑生成邏輯
 *
 * 🔁 Route 分類原則：
 * - CLUB_*_ROUTER：表示店舖操作使用（如會員、量測）
 * - EMPLOYEE_*：教練／員工相關操作
 * - DOCUMENTS_*：會籍文書合約（各種申請書）
 * - ACCOUNT_BOOK_*：金流與會計作業
 *
 * 📍 特殊命名說明：
 * - READONLY_CONTRACT：純檢視狀態，無編輯行為（但有完整資料）
 * - PAGE：包含複合型 routing 的條件轉址頁（通常含 tabs / page / ID）
 * - BASE：模組基礎路徑（一般為目錄）
 *
 * 🧩 注意事項：
 * - 所有 GET_*_PATH() 方法僅產出 path string，不含實際參數檢查邏輯
 * - 路由不要混用語系（建議全部使用英文），避免前端與後台不同調
 * - tabs 請統一來自 @icoach/constants/ ...等，如需要再且且新增頁籤請參考 *_TABS
 * - 函數中的參數不應給預設值，避免未來維護時出現問題
 *     GET_CONTRACT_PATH(peopleType, referenceID, contractID) ✅
 *     GET_CONTRACT_PATH(peopleType, referenceID, contractID = 0) ❌（避免 default）
 *
 * 📚 設計參考：
 * - 如需新增頁籤型路由，優先參考 TABS_PAGE 或 TABS 相關範例
 * - 若頁面需支援歷史紀錄與合約內容，參考：MEMBERSHIP.CONTRACT / HISTORY
 *
 * ✏️ 作者說明：
 * - e.g., // [2025/05/05] Allen 說明
 *
 * ─────────────────────────────────────────────────────────────
 */

/** █ ICoach 登入 **/
export const CLUB_LOGIN_ROUTER = Object.freeze({
    ROOT : `/club`,
    BASE : `/club/`,
    LOGIN: `/club/login/`,
});

/** █ ICoach or 教練 忘記密碼,教練登入 **/
export const EMPLOYEE_LOGIN_ROUTER = Object.freeze({
    BASE                : `/login/`,
    COACH_LOGIN         : `/login/coach/`,
    COACH_RESET         : `/login/coach/reset-password/:token/`,
    CLUB_RESET          : `/login/club/reset-password/:token/`,
    GET_COACH_RESET_PATH: (token) => `/login/coach/reset-password/${token}/`,
    GET_CLUB_RESET_PATH : (token) => `/login/club/reset-password/${token}/`,
});

/** █ 通知 **/
export const NOTIFICATIONS_ROUTER = Object.freeze({
    BASE             : `/club-notifications/`,
    ENTIRE           : `/club-notifications/entire/`,
    TRANSFERIN       : `/club-notifications/doc-transferin/`,
    TRANSFEROUT      : `/club-notifications/doc-transferout/`,
    MEMBERSHIP_EXPIRE: `/club-notifications/doc-membership-expire/`,
    INVOICE          : `/club-notifications/invoice/`,
    VERSION_UPDATE   : `/club-notifications/version-update/`,
    TEMP_DOC         : `/club-notifications/doc-temporariness/`,
    CYCLE_PURCHASE   : `/club-notifications/healthy-cycle-purchase/`,
});

/** █ 首頁來店會員打卡 **/
export const CLUB_HOME_ROUTER = Object.freeze({
    BASE     : `/club/home/`,
    ARRIVE   : `/club/home/${LANDING_NAVIGATION_TABS.ARRIVE}/`,
    TABS_PAGE: `/club/home/:tabs(${LANDING_NAVIGATION_TABS.ARRIVE})/`,
});

/** █ 預約相關**/
export const CLUB_APPOINTMENT_ROUTER = Object.freeze({
    BASE                : `/club/appointment/`,
    INFORMATION_PAGE    : `/club/appointment/:appointmentID(\\d+)/information/`,
    GET_INFORMATION_PATH: (appointmentID) => `/club/appointment/${appointmentID}/information/`,
    TABS_PAGR           : `/club/appointment/:tabs(${LANDING_NAVIGATION_TABS.TIME_SCHEDULING}|${LANDING_NAVIGATION_TABS.OVERVIEW})/`,
    GET_TABS_PATH       : (tabs) => `/club/appointment/${tabs}/`,
});

/** █ 教練每日班表 **/
export const CLUB_EMPLOYEE_SCHEDULE_ROUTER = Object.freeze({
    BASE            : `/club/employee/`,
    TABS_PAGE       : `/club/employee/:tabs(${LANDING_NAVIGATION_TABS.SHIFT_SCHEDULING})/`,
    SHIFT_SCHEDULING: `/club/employee/${LANDING_NAVIGATION_TABS.SHIFT_SCHEDULING}/`,
});

/** █ 營運報表 **/
export const CLUB_DATA_ANALYSIS_ROUTER = Object.freeze({
    BASE            : `/club/data-analysis/`,
    TABS            : `/club/data-analysis/:tabs(goal|membership|service|guest-production|sales|employees|task-matter)?/`,
    GOAL            : `/club/data-analysis/goal/`,
    MEMBERSHIP      : `/club/data-analysis/membership/`,
    SERVICE         : `/club/data-analysis/service/`,
    GUEST_PRODUCTION: `/club/data-analysis/guest-production/`,
    SALES           : `/club/data-analysis/sales/`,
    EMPLOYEES       : `/club/data-analysis/employees/`,
    TASK_MATTER     : `/club/data-analysis/task-matter/`,
});

/** █ 量身管理相關 **/
export const CLUB_INBODY_ROUTER = Object.freeze({
    BASE                          : `/club/inbody/`,
    HISTORY                       : `/club/inbody/history/`,
    HISTORY_PAGE                  : `/club/inbody/history/:referenceType(\\d+)?/:referenceID(\\d+)?/`,
    GET_HISTORY_REFERENCETYPE_PAGE: (referenceType) => `/club/inbody/history/${referenceType}/`,
    GET_HISTORY_PATH              : (referenceType, referenceID) => `/club/inbody/history/${referenceType}/${referenceID}/`,
    INTERVIEW_PAGE                : `/club/inbody/interview/:referenceType(\\d+)/:tabs(${IN_BODY_INTERVIEW_TABS.SUMMARY}|${IN_BODY_INTERVIEW_TABS.MEASURE}|${IN_BODY_INTERVIEW_TABS.NOTES}|${IN_BODY_INTERVIEW_TABS.COMPARE})/:referenceID(\\d+)/`,
    GET_COMPARE_PATH              : (referenceID) => `/club/inbody/interview/${MEASURE_REFERENCE_TYPE.MEMBER}/compare/${referenceID}/`,
    GET_MEASURE_PATH              : (referenceID) => `/club/inbody/interview/${MEASURE_REFERENCE_TYPE.MEMBER}/measure/${referenceID}/`,
    GET_NOTES_PATH                : (referenceID) => `/club/inbody/interview/${MEASURE_REFERENCE_TYPE.MEMBER}/notes/${referenceID}/`,
    GET_INTERVIEW_PATH            : (tabs, referenceID) => `/club/inbody/interview/${MEASURE_REFERENCE_TYPE.MEMBER}/${tabs}/${referenceID}/`,
    EMPLOYEE_INTERVIEW_PAGE       : `/club/inbody/employee-interview/:referenceType(${MEASURE_REFERENCE_TYPE.EMPLOYEE})/:tabs(${IN_BODY_INTERVIEW_TABS.MEASURE}|${IN_BODY_INTERVIEW_TABS.COMPARE})/:referenceID(\\d+)/`,
    GET_EMPLOYEE_INTERVIEW_PATH   : (tabs, referenceID) => `/club/inbody/employee-interview/${MEASURE_REFERENCE_TYPE.EMPLOYEE}/${tabs}/${referenceID}/`,
    OVERVIEW_FILTER_PAGE          : `/club/inbody/overview/filter/:quickType(1|2|3|4)`,
    GET_OVERVIEW_FILTER_PATH      : (quickType) => `/club/inbody/overview/filter/${quickType}/`,
});

/** █ 新會籍文書相關 **/
export const CLUB_DOCUMENTS_ROUTER = Object.freeze({
    BASE: `/club/documents/`,
    /** ▌會籍申請 **/
    MEMBERSHIP: {
        BASE             : `/club/documents/membership/`,
        HISTORY          : `/club/documents/membership/:peopleType(\\d+)/:referenceID(\\d+)/history/`,
        CONTRACT         : `/club/documents/membership/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_CONTRACT: `/club/documents/membership/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        PAGE             : `/club/documents/membership/:peopleType(\\d+)?/:referenceID(\\d+)?/:page(history|contract|readonly-contract)?/:contractID(\\d+)?/`,
        GET_HISTORY_PATH : (peopleType, referenceID) => `/club/documents/membership/${peopleType}/${referenceID}/history/`,
        GET_CONTRACT_PATH: (peopleType, referenceID, contractID) => `/club/documents/membership/${peopleType}/${referenceID}/contract/${contractID}/`,
        GET_READONLY_PATH: (peopleType, referenceID, contractID) => `/club/documents/membership/${peopleType}/${referenceID}/readonly-contract/${contractID}/`,
    },
    /** ▌會籍暫停 **/
    SUSPENSION: {
        BASE               : `/club/documents/membership-suspension/`,
        HISTORY            : `/club/documents/membership-suspension/:peopleType(\\d+)/:referenceID(\\d+)/history/`,
        CONTRACT           : `/club/documents/membership-suspension/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_CONTRACT  : `/club/documents/membership-suspension/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        PAGE               : `/club/documents/membership-suspension/:peopleType(\\d+)?/:referenceID(\\d+)?/:page(history|contract|readonly-contract)?/:contractID(\\d+)?/`,
        GET_PEOPLETYPE_PATH: (peopleType) => `/club/documents/membership-suspension/${peopleType}/`,
        GET_HISTORY_PATH   : (peopleType, referenceID) => `/club/documents/membership-suspension/${peopleType}/${referenceID}/history/`,
        GET_CONTRACT_PATH  : (peopleType, referenceID, contractID) => `/club/documents/membership-suspension/${peopleType}/${referenceID}/contract/${contractID}/`,
        GET_READONLY_PATH  : (peopleType, referenceID, contractID) => `/club/documents/membership-suspension/${peopleType}/${referenceID}/readonly-contract/${contractID}/`,
    },
    /** ▌終止申請書 **/
    QUIT: {
        BASE                        : `/club/documents/membership-quit/`,
        HISTORY                     : `/club/documents/membership-quit/:peopleType(\\d+)/:referenceID(\\d+)/history/`,
        CONTRACT                    : `/club/documents/membership-quit/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_CONTRACT           : `/club/documents/membership-quit/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        PAGE                        : `/club/documents/membership-quit/:peopleType(\\d+)?/:referenceID(\\d+)?/:page(history|contract|readonly-contract)?/:contractID(\\d+)?/`,
        GET_HISTORY_ONLY_PEOPLE_PATH: (peopleType) => `/club/documents/membership-quit/${peopleType}/`,
        GET_HISTORY_PATH            : (peopleType, referenceID) => `/club/documents/membership-quit/${peopleType}/${referenceID}/history/`,
        GET_CONTRACT_PATH           : (peopleType, referenceID, contractID) => `/club/documents/membership-quit/${peopleType}/${referenceID}/contract/${contractID}/`,
        GET_READONLY_PATH           : (peopleType, referenceID, contractID) => `/club/documents/membership-quit/${peopleType}/${referenceID}/readonly-contract/${contractID}/`,
    },
    /** ▌會籍轉讓 **/
    TRANSFEROVER: {
        BASE               : `/club/documents/membership-transferover/`,
        HISTORY            : `/club/documents/membership-transferover/:peopleType(\\d+)/:referenceID(\\d+)/history/`,
        CONTRACT           : `/club/documents/membership-transferover/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_CONTRACT  : `/club/documents/membership-transferover/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        PAGE               : `/club/documents/membership-transferover/:peopleType(\\d+)?/:referenceID(\\d+)?/:page(history|contract|readonly-contract)?/:contractID(\\d+)?/`,
        GET_PEOPLETYPE_PATH: (peopleType) => `/club/documents/membership-transferover/${peopleType}/`,
        GET_HISTORY_PATH   : (peopleType, referenceID) => `/club/documents/membership-transferover/${peopleType}/${referenceID}/history/`,
        GET_CONTRACT_PATH  : (peopleType, referenceID, contractID) => `/club/documents/membership-transferover/${peopleType}/${referenceID}/contract/${contractID}/`,
        GET_READONLY_PATH  : (peopleType, referenceID, contractID) => `/club/documents/membership-transferover/${peopleType}/${referenceID}/readonly-contract/${contractID}/`,
    },
    /** ▌移入申請書 - 國內移入 or 尚未移入/已移入 **/
    TRANSFERIN: {
        BASE                : `/club/documents/membership-transferin/`,
        HISTORY             : `/club/documents/membership-transferin/:enteredType(${TRANSFERIN_ENTEREDTYPE.UNENTERED}|${TRANSFERIN_ENTEREDTYPE.ENTERED})/:peopleType(\\d+)/:referenceID(\\d+)/history/`,
        CONTRACT            : `/club/documents/membership-transferin/:enteredType(${TRANSFERIN_ENTEREDTYPE.UNENTERED}|${TRANSFERIN_ENTEREDTYPE.ENTERED})/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_CONTRACT   : `/club/documents/membership-transferin/:enteredType(${TRANSFERIN_ENTEREDTYPE.UNENTERED}|${TRANSFERIN_ENTEREDTYPE.ENTERED})/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        PAGE                : `/club/documents/membership-transferin/:enteredType(${TRANSFERIN_ENTEREDTYPE.UNENTERED}|${TRANSFERIN_ENTEREDTYPE.ENTERED})?/:peopleType(\\d+)?/:referenceID(\\d+)?/:page(history|contract|readonly-contract)?/:contractID(\\d+)?/`,
        CONTRACT_UNENTERED  : `/club/documents/membership-transferin/${TRANSFERIN_ENTEREDTYPE.UNENTERED}/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        CONTRACT_ENTERED    : `/club/documents/membership-transferin/${TRANSFERIN_ENTEREDTYPE.ENTERED}/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_ENTERED    : `/club/documents/membership-transferin/${TRANSFERIN_ENTEREDTYPE.ENTERED}/:peopleTyp(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        GET_ENTEREDTYPE_PATH: (enteredType) => `/club/documents/membership-transferin/${enteredType}/`,
        GET_PEOPLETYPE_PATH : (enteredType, peopleType) => `/club/documents/membership-transferin/${enteredType}/${peopleType}/`,
        GET_REFERENCEID_PATH: (enteredType, peopleType, referenceID) => `/club/documents/membership-transferin/${enteredType}/${peopleType}/${referenceID}`,
        GET_HISTORY_PATH    : (enteredType, peopleType, referenceID) =>`/club/documents/membership-transferin/${enteredType}/${peopleType}/${referenceID}/history/`,
        GET_CONTRACT_PATH   : (enteredType, peopleType, referenceID, contractID) =>`/club/documents/membership-transferin/${enteredType}/${peopleType}/${referenceID}/contract/${contractID}/`,
        GET_READONLY_PATH   : (enteredType, peopleType, referenceID, contractID) =>`/club/documents/membership-transferin/${enteredType}/${peopleType}/${referenceID}/readonly-contract/${contractID}/`,
    },
    /** ▌移出申請書 - 國內移出 **/
    TRANSFEROUT: {
        BASE                : `/club/documents/membership-transferout/`,
        HISTORY             : `/club/documents/membership-transferout/:moveoutType(${TRANSFEROUT_MOVETYPE.UNMOVEOUT}|${TRANSFEROUT_MOVETYPE.MOVEOUT})/:peopleType(\\d+)/:referenceID(\\d+)/history/`,
        CONTRACT            : `/club/documents/membership-transferout/:moveoutType(${TRANSFEROUT_MOVETYPE.UNMOVEOUT}|${TRANSFEROUT_MOVETYPE.MOVEOUT})/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_CONTRACT   : `/club/documents/membership-transferout/:moveoutType(${TRANSFEROUT_MOVETYPE.UNMOVEOUT}|${TRANSFEROUT_MOVETYPE.MOVEOUT})/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        PAGE                : `/club/documents/membership-transferout/:moveoutType(${TRANSFEROUT_MOVETYPE.UNMOVEOUT}|${TRANSFEROUT_MOVETYPE.MOVEOUT})?/:peopleType(\\d+)?/:referenceID(\\d+)?/:page(history|contract|readonly-contract)?/:contractID(\\d+)?/`,
        CONTRACT_UNMOVEOUT  : `/club/documents/membership-transferout/${TRANSFEROUT_MOVETYPE.UNMOVEOUT}/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_UNMOVEOUT  : `/club/documents/membership-transferout/${TRANSFEROUT_MOVETYPE.UNMOVEOUT}/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        READONLY_MOVEOUT    : `/club/documents/membership-transferout/${TRANSFEROUT_MOVETYPE.MOVEOUT}/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        GET_MOVEOUTTYPE_PATH: (moveoutType) => `/club/documents/membership-transferout/${moveoutType}/`,
        GET_PEOPLETYPE_PATH : (moveoutType, peopleType) => `/club/documents/membership-transferout/${moveoutType}/${peopleType}/`,
        GET_REFERENCEID_PATH: (moveoutType, peopleType, referenceID) => `/club/documents/membership-transferout/${moveoutType}/${peopleType}/${referenceID}/`,
        GET_HISTORY_PATH    : (moveoutType, peopleType, referenceID) => `/club/documents/membership-transferout/${moveoutType}/${peopleType}/${referenceID}/history/`,
        GET_CONTRACT_PATH   : (moveoutType, peopleType, referenceID, contractID) => `/club/documents/membership-transferout/${moveoutType}/${peopleType}/${referenceID}/contract/${contractID}/`,
        GET_READONLY_PATH   : (moveoutType, peopleType, referenceID, contractID) => `/club/documents/membership-transferout/${moveoutType}/${peopleType}/${referenceID}/readonly-contract/${contractID}/`,
    },
    /** ▌健康申告書 **/
    HEALTHDECLARATION: {
        BASE               : `/club/documents/healthdeclaration/`,
        HISTORY            : `/club/documents/healthdeclaration/:peopleType(\\d+)/:referenceID(\\d+)/history/`,
        CONTRACT           : `/club/documents/healthdeclaration/:peopleType(\\d+)/:referenceID(\\d+)/contract/:contractID(\\d+)/`,
        READONLY_CONTRACT  : `/club/documents/healthdeclaration/:peopleType(\\d+)/:referenceID(\\d+)/readonly-contract/:contractID(\\d+)/`,
        PAGE               : `/club/documents/healthdeclaration/:peopleType(\\d+)?/:referenceID(\\d+)?/:page(history|contract|readonly-contract)?/:contractID(\\d+)?/`,
        GET_PEOPLETYPE_PATH: (peopleType) => `/club/documents/healthdeclaration/${peopleType}/`,
        GET_HISTORY_PATH   : (peopleType, referenceID) => `/club/documents/healthdeclaration/${peopleType}/${referenceID}/history/`,
        GET_CONTRACT_PATH  : (peopleType, referenceID, contractID) => `/club/documents/healthdeclaration/${peopleType}/${referenceID}/contract/${contractID}/`,
        GET_READONLY_PATH  : (peopleType, referenceID, contractID) => `/club/documents/healthdeclaration/${peopleType}/${referenceID}/readonly-contract/${contractID}/`,
    },
});

/** █ 會員管理 **/
export const CLUB_MEMBERS_ROUTER = Object.freeze({
    BASE         : `/club/members/`,
    TABS         : `/club/members/:tabs(${MEMBERS_TABS.MEMBER_OVERVIEW}|${MEMBERS_TABS.RENT_CABINET}|${MEMBERS_TABS.GIFT}|${MEMBERS_TABS.SMARTFITNESSBAND})/`,
    GET_TABS_PATH: (tabs) => `/club/members/${tabs}/`,
    /** ▌會員總覽 **/
    MEMBER_OVERVIEW: {
        BASE          : `/club/members/${MEMBERS_TABS.MEMBER_OVERVIEW}/`,
        PAGE          : `/club/members/:tabs(${MEMBERS_TABS.MEMBER_OVERVIEW})/`,
        GET_TABS_PATH : (tabs) => `/club/members/${MEMBERS_TABS.MEMBER_OVERVIEW}/${tabs}/`,
        NORMAL_PAGE   : `/club/members/member-overview/:subTabs(normal)/`,
        QUICK_PAGE    : `/club/members/member-overview/:subTabs(quick)/:quickType([\\d]+)?/`,
        GET_QUICK_PATH: (type)=> `/club/members/member-overview/quick/${type}/`,
    },
    /** ▌會員資訊頁 **/
    MEMBER_DETAIL: {
        BASE         : `/club/members/`,
        PAGE         : `/club/members/:memberID(\\d+)/detail/:tab(${MEMBERS_MEMBER_DETAIL_TABS.WORKOUT}|${MEMBERS_MEMBER_DETAIL_TABS.BASIC}|${MEMBERS_MEMBER_DETAIL_TABS.MEMBERSHIP}|${MEMBERS_MEMBER_DETAIL_TABS.INBODY}|${MEMBERS_MEMBER_DETAIL_TABS.CONTACT_RECORD}|${MEMBERS_MEMBER_DETAIL_TABS.HC}|${MEMBERS_MEMBER_DETAIL_TABS.RENT_CABINET}|${MEMBERS_MEMBER_DETAIL_TABS.OTHER}|${MEMBERS_MEMBER_DETAIL_TABS.SMARTFITNESSBAND})/`,
        GET_TABS_PATH: (memberID, tabs) => `/club/members/${memberID}/detail/${tabs}/`,
    },
    /** ▌會員快速名單 **/
    MEMBER_QUICK: {
        BASE: ``,
    },
    /** ▌租櫃管理 **/
    CABINET: {
        BASE                           : `/club/members/${MEMBERS_TABS.RENT_CABINET}/`,
        DOCUMENT                       : `/club/members/${MEMBERS_TABS.RENT_CABINET}/document/:rentCabinetID(\\d+)/`,
        CONTRACT                       : `/club/members/${MEMBERS_TABS.RENT_CABINET}/contract/:rentCabinetID(\\d+)/`,
        REVIEW                         : `/club/members/${MEMBERS_TABS.RENT_CABINET}/contract-review/:rentCabinetID(\\d+)/`,
        PAGE                           : `/club/members/:tabs(${MEMBERS_TABS.RENT_CABINET})/:subTabs(overview|document)/:rentCabinetID(\\d+)?/`,
        GET_TABS_PATH                  : (tabs) => `/club/members/${MEMBERS_TABS.RENT_CABINET}/${tabs}/`,
        GET_CONTRACT_RENTCABINETID_PATH: (tabs, rentCabinetID) => `/club/members/${MEMBERS_TABS.RENT_CABINET}/${tabs}/${rentCabinetID}/`,
    },
    /** ▌贈品 **/
    GIFT: {
        BASE         : `/club/members/${MEMBERS_TABS.GIFT}/`,
        PAGE         : `/club/members/:tabs(${MEMBERS_TABS.GIFT})/:subTabs(overview|dispatch-record|dispatch-metrics)/`,
        GET_TABS_PATH: (tabs) => `/club/members/${MEMBERS_TABS.GIFT}/${tabs}/`,
    },
    /** ▌智能手錶管理 **/
    SMARTFITNESSBAND: {
        BASE                        : `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/`,
        PAGE                        : `/club/members/:tabs(${MEMBERS_TABS.SMARTFITNESSBAND})/:subTabs(overview)/`,
        GET_TABS_PATH               : (tabs) => `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/${tabs}/`,
        CONTRACT_PAGE               : `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/:page(contract)/:contractID(\\d+)/`,
        CONTRACT_PATH               : (contractID)=> `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/contract/${contractID}/`,
        CONTRACT_DETAIL_PAGE        : `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/:page(readonly-contract)/:contractID(\\d+)/:tabs(main|history)/`,
        CONTRACT_DETAIL_MAIN_PAGE   : `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/:page(readonly-contract)/:contractID(\\d+)/main/`,
        CONTRACT_DETAIL_MAIN_PATH   : (contractID) => `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/readonly-contract/${contractID}/main/`,
        CONTRACT_DETAIL_HISTORY_PAGE: `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/:page(readonly-contract)/:contractID(\\d+)/history/`,
        CONTRACT_DETAIL_HISTORY_PATH: (contractID) =>`/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/readonly-contract/${contractID}/history/`,
        CONTRACT_QUIT_PAGE          : `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/:page(contract)/:contractID(\\d+)/quit/`,
        CONTRACT_QUIT_PATH          : (contractID) => `/club/members/${MEMBERS_TABS.SMARTFITNESSBAND}/contract/${contractID}/quit/`,
    },
});

/** ▌HC 循環訂單 **/
export const DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER = Object.freeze({
    BASE    : `/club/documents/healthy-cycle-purchase/`,
    OVERVIEW: `/club/documents/healthy-cycle-purchase/overview/`,
    CREATE  : `/club/documents/healthy-cycle-purchase/create/`,
    /** ▌訂單總覽與詳情 **/
    DETAIL: {
        BASE            : `/club/documents/healthy-cycle-purchase/:memberID(\\d+)/:cycleOrderID(\\d+)/`,
        PAGE            : `/club/documents/healthy-cycle-purchase/:memberID(\\d+)/:cycleOrderID(\\d+)/:tabs(main|history|future)/`,
        MAIN            : `/club/documents/healthy-cycle-purchase/:memberID(\\d+)/:cycleOrderID(\\d+)/main/`,
        HISTORY         : `/club/documents/healthy-cycle-purchase/:memberID(\\d+)/:cycleOrderID(\\d+)/history/`,
        FUTURE          : `/club/documents/healthy-cycle-purchase/:memberID(\\d+)/:cycleOrderID(\\d+)/future/`,
        GET_PATH        : (memberID, cycleOrderID, tabName) => `/club/documents/healthy-cycle-purchase/${memberID}/${cycleOrderID}/${tabName}/`,
        GET_MAIN_PATH   : (memberID, cycleOrderID) => `/club/documents/healthy-cycle-purchase/${memberID}/${cycleOrderID}/main/`,
        GET_HISTORY_PATH: (memberID, cycleOrderID) => `/club/documents/healthy-cycle-purchase/${memberID}/${cycleOrderID}/history/`,
        GET_FUTURE_PATH : (memberID, cycleOrderID) => `/club/documents/healthy-cycle-purchase/${memberID}/${cycleOrderID}/future/`,
    },
    /** ▌變更單（已簡化，僅處理訂單內容變更） **/
    CHANGE: {
        BASE    : `/club/documents/healthy-cycle-purchase/:cycleOrderID(\\d+)/:effectID(\\d+)/change/`,
        GET_PATH: (cycleOrderID, effectID) => `/club/documents/healthy-cycle-purchase/${cycleOrderID}/${effectID}/change/`,
    },
    /** ▌暫停申請（獨立申請表單） **/
    SUSPEND: {
        BASE    : `/club/documents/healthy-cycle-purchase/:cycleOrderID(\\d+)/:effectID(\\d+)/suspend/`,
        GET_PATH: (cycleOrderID, effectID) => `/club/documents/healthy-cycle-purchase/${cycleOrderID}/${effectID}/suspend/`,
    },
    /** ▌終止申請（獨立申請表單） **/
    TERMINATE: {
        BASE    : `/club/documents/healthy-cycle-purchase/:cycleOrderID(\\d+)/:effectID(\\d+)/terminate/`,
        GET_PATH: (cycleOrderID, effectID) => `/club/documents/healthy-cycle-purchase/${cycleOrderID}/${effectID}/terminate/`,
    },
    /** ▌恢復申請（獨立申請表單） **/
    RESUME: {
        BASE    : `/club/documents/healthy-cycle-purchase/:cycleOrderID(\\d+)/:effectID(\\d+)/resume/`,
        GET_PATH: (cycleOrderID, effectID) => `/club/documents/healthy-cycle-purchase/${cycleOrderID}/${effectID}/resume/`,
    },
    /** ▌舊版相容路由（過渡期使用） **/
    LEGACY_EFFECT: {
        BASE    : `/club/documents/healthy-cycle-purchase/effect/:memberID(\\d+)/:cycleOrderID(\\d+)/:effectID(\\d+)/`,
        GET_PATH: (memberID, cycleOrderID, effectID) => `/club/documents/healthy-cycle-purchase/effect/${memberID}/${cycleOrderID}/${effectID}/`,
    },
});


/** █ 點數管理 **/
export const POINTS_POINTS_REDEEM_OVERVIEW_ROUTER = `/club/points/points-redeem/overview/`;
export const POINTS_PRODUCT_GIFT_OVERVIEW_ROUTER  = `/club/points/points-product-gift/overview/`;

/** █ 排行榜 **/
export const MEASURE_HEADERBOARD_ROUTER = `/club/measure/headerboard/`;

/** █ POS 系統 **/
export const CLUB_POS_ROUTER = Object.freeze({
    BASE  : `/club-pos/`,
    SCREEN: `/club-pos/screen/`,
});

/** █ 店鋪庫存管理系統 **/
// 從 clubInventory 模組重新導出，方便統一使用
export { CLUB_INVENTORY_ROUTER } from "@clubInventory/router/routerPath";

/** █ 店鋪管理 **/
export const HUMAN_RESOURCES_TABS                      = `/club/human-resources/:tabs(employees-overview|roles-permissions-overview)/`;
export const HUMAN_RESOURCES_EMPLOYEES_OVERVIEW        = `/club/human-resources/employees-overview/`;
export const HUMAN_RESOURCES_ROLES_PERMISSION_OVERVIEW = `/club/human-resources/roles-permissions-overview/`;

export const HUMAN_RESOURCES_ADD                         = `/club/human-resources/employees/add/`;
export const HUMAN_RESOURCES_INFORMATION_EMPLOYEEID      = `/club/human-resources/employees/information/:employeeID/`;
export const HUMAN_RESOURCES_INFORMATION_EMPLOYEEID_PATH = (employeeID) => `/club/human-resources/employees/information/${employeeID}/`;

export const SYSTEM_CONFIGURATION_TABS   = `/club/system-configuration/:tabs(basic|others)/`;
export const SYSTEM_CONFIGURATION_BASIC  = `/club/system-configuration/basic/`;
export const SYSTEM_CONFIGURATION_OTHERS = `/club/system-configuration/others/`;
export const CLUB_CLOSED_CONFIGURATION   = `/club/club-closed/configuration/`;

/** █ 營收管理 **/
export const ACCOUNT_BOOK_ORDER_CREATE              = `/club/account-book/orders/create/`;
export const ACCOUNT_BOOK_REMIT_CREATE              = `/club/account-book/remit/create/`;
export const ACCOUNT_BOOK_ORDERS_OVERVIEW           = `/club/account-book/:tabs(orders)/overview/`;
export const ACCOUNT_BOOK_ORDERS_OVERVIEW_URL       = `/club/account-book/orders/overview/`;
export const ACCOUNT_BOOK_REMIT_ORDERS_OVERVIEW     = `/club/account-book/:tabs(remit-orders)/overview/`;
export const ACCOUNT_BOOK_REMIT_ORDERS_OVERVIEW_URL = `/club/account-book/remit-orders/overview/`;
export const ACCOUNT_BOOK_REMIT_ALLOWANCE           = `/club/account-book/remit/:remittanceID/allowance-history/`;
export const ACCOUNT_BOOK_REMIT_ALLOWANCE_URL       = (remittanceID) => `/club/account-book/remit/${remittanceID}/allowance-history/`;
export const ACCOUNT_BOOK_SITE_ORDER_STAT           = `/club/account-book/:tabs(site-order-stat)/overview/`;
export const ACCOUNT_BOOK_SITE_ORDER_STAT_URL       = '/club/account-book/site-order-stat/overview/';
export const ACCOUNT_BOOK_ALL_ORDER_STAT            = `/club/account-book/:tabs(all-order-stat)/overview/`;
export const ACCOUNT_BOOK_ALL_ORDER_STAT_URL        = `/club/account-book/all-order-stat/overview/`;
export const ACCOUNT_BOOK_BULK_DEBIT                = `/club/account-book/:tabs(bulk-debit)/`;
export const ACCOUNT_BOOK_BULK_DEBIT_URL            = `/club/account-book/bulk-debit/`;

export const ACCOUNT_BOOK_ORDERS_ORDER         = `/club/account-book/orders/:orderID/`;
export const ACCOUNT_BOOK_ORDERS_ORDER_URL     = (orderID) => `/club/account-book/orders/${orderID}/`;
export const ACCOUNT_BOOK_ORDERS_ALLOWANCE     = `/club/account-book/orders/:orderID/allowance-history/`;
export const ACCOUNT_BOOK_ORDERS_ALLOWANCE_URL = (orderID) => `/club/account-book/orders/${orderID}/allowance-history/`;

export const ACCOUNT_BOOK_TABS_SETTLE_CASH_PRICE     = `/club/account-book/settle-cash-price/:date/`;
export const ACCOUNT_BOOK_TABS_SETTLE_CASH_PRICE_URL = (date) => `/club/account-book/settle-cash-price/${date}/`;

export const ACCOUNT_BOOK_BULK_DEBIT_BILLING_DETAIL     = `/club/account-book/:tabs(bulk-debit)/billing/:billingID`;
export const ACCOUNT_BOOK_BULK_DEBIT_BILLING_DETAIL_URL = (billingID) => `/club/account-book/bulk-debit/billing/${billingID}`;

export const ACCOUNT_BOOK_OVERVIEW_FILTER = (num) => `/club/account-book/overview/filter/${num}/`;
export const ACCOUNT_BOOK_TRIPLICATE_INVOICE_ALLOWANCE = `/club/account-book/:tabs(triplicate-invoice-allowance)/overview/`;
export const ACCOUNT_BOOK_TRIPLICATE_INVOICE_ALLOWANCE_OVERVIEW = `/club/account-book/triplicate-invoice-allowance/overview/`;

// /** █ 進貨管理 **/
export const MEMBERS_MEMBER_DETAIL_URL = (memberID) => `/club/members/${memberID}/detail/`;


/** █ 顧客管理 **/
export const NON_MEMBER_ROUTER = Object.freeze({
    BASE: `/club/non-member/`,
    TABS: {
        OVERVIEW_PAGE: `/club/non-member/:tabs(customer|prospect|temporaries)/overview/`,
    },
    // 顧客
    CUSTOMER: {
        BASE         : `/club/non-member/customer/`,
        OVERVIEW     : `/club/non-member/customer/overview/`,
        ADD_BASE     : `/club/non-member/customer/add/`,
        ADD_PAGE     : `/club/non-member/customer/add/:id(\\d+)?/`,
        GET_ADD_PATH : (id) => `/club/non-member/customer/add/${id}/`,
        INFO_BASE    : `/club/non-member/customer/info/`,
        INFO_PAGE    : `/club/non-member/customer/info/:id(\\d+)?/`,
        GET_INFO_PATH: (id) => `/club/non-member/customer/info/${id}/`
    },
    // 名單
    PROSPECT: {
        BASE         : `/club/non-member/prospect/`,
        OVERVIEW     : `/club/non-member/prospect/overview/`,
        ADD_BASE     : `/club/non-member/prospect/add/`,
        ADD_PAGE     : `/club/non-member/prospect/add/:id(\\d+)?/`,
        GET_ADD_PATH : (id) => `/club/non-member/prospect/add/${id}/`,
        INFO_BASE    : `/club/non-member/prospect/info/`,
        INFO_PAGE    : `/club/non-member/prospect/info/:id(\\d+)?/`,
        GET_INFO_PATH: (id) => `/club/non-member/prospect/info/${id}/`,
    },
    // 暫存(名單/顧客) - 此功能是(名單/顧客)分組名單做群組化，店舖開會打電話快速討論使用(切勿隨意修改中文名稱，這是業務流程，顯示照業務流程，背後程式邏輯明確好理解即可)
    TEMPORARIES: {
        BASE           : `/club/non-member/temporaries/`,
        OVERVIEW       : `/club/non-member/temporaries/overview/`,
        INFO_PAGE      : `/club/non-member/temporaries/:entity(customer|prospect)/info/:id(\\d+)/`,
        GET_ENTITY_PATH: (type,id) => `/club/non-member/temporaries/${type}/info/${id}/`
    },
    // 顧客製造 - 顧名思義就是工作排程針對去找業績的計畫排程
    ACTIONPLAN:{
        OVERVIEW: `/club/non-member/action-plan/overview/`
    }
});
